/**
 * Licensed Materials - Property of IBM
 * 5737-M66, 5900-AAA, 5900-A0N, 5725-S86, 5737-I75
 * © Copyright IBM Corp. 2020, 2023 All Rights Reserved.
 * US Government Users Restricted Rights - Use, duplication, or disclosure
 * restricted by GSA ADP Schedule Contract with IBM Corp.
 */
import { Route } from 'react-router-dom';
import PropTypes from 'prop-types';
import React from 'react';
import isEmpty from 'lodash/isEmpty';

import Unauthorized from '../Unauthorized/Unauthorized';
import { ACCESS_CONTROLS } from '../../constants/constants';

const propTypes = {
  /** the main component to render inside the route */
  component: PropTypes.elementType.isRequired,
  /** the list of the current users access permissions */
  accessControls: PropTypes.arrayOf(PropTypes.string),
  /** the specific access control needed to view the page */
  necessaryAccess: PropTypes.arrayOf(PropTypes.oneOf(Object.values(ACCESS_CONTROLS))),
  tenantId: PropTypes.string,
  docURL: PropTypes.string,
};

const defaultProps = {
  accessControls: null,
  necessaryAccess: [],
  tenantId: null,
  docURL: null,
};

const renderMergedProps = (component, ...rest) => {
  const finalProps = Object.assign({}, ...rest);
  return React.createElement(component, finalProps);
};

/**
 * This component provides access control and renders another component within a route (preventing performance issues). see this recommendation:
 * https://github.com/ReactTraining/react-router/issues/4105
 */
class PropsRoute extends React.Component {
  handleRender = routeProps => {
    // Have to throw away the parent's route based properties: computedMatch, history, location, match, path or else things go wrong with routng
    // Throw away necessaryAccess because it's not using outside of HomeApp
    const {
      component,
      computedMatch: computedMatch__, // eslint-disable-line react/prop-types
      history: history__,
      match: match__,
      path: path__, // eslint-disable-line  react/prop-types
      necessaryAccess: necessaryAccess__, // eslint-disable-line react/prop-types
      ...rest
    } = this.props;
    return renderMergedProps(component, routeProps, rest);
  };

  render() {
    const {
      component: component__,
      children: children__, // eslint-disable-line react/prop-types
      accessControls,
      necessaryAccess,
      tenantId,
      location,
      docURL,
      ...rest
    } = this.props;
    return !isEmpty(necessaryAccess) && // both the necessary access
      !isEmpty(accessControls) && // and access controls need to be set
      !accessControls.find(accessControl => necessaryAccess.includes(accessControl)) ? (
      <Unauthorized
        title="IBM Maximo Application Suite"
        URL={location?.pathname}
        tenantId={tenantId}
        docURL={docURL}
      />
    ) : (
      <Route // eslint-disable-next-line react/jsx-props-no-spreading
        {...rest}
        tenantId={tenantId}
        render={this.handleRender}
      />
    );
  }
}

PropsRoute.propTypes = propTypes;
PropsRoute.defaultProps = defaultProps;

export default PropsRoute;
